import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

/**
 * Browser-side TikTok identity helpers.
 *
 * Responsibilities:
 *  - capture the `ttclid` click id from the landing URL into a first-party
 *    cookie so our server routes can attach it to the Events API payload;
 *  - maintain a stable first-party `tt_external_id` cookie used as TikTok's
 *    `external_id` (read both here for the pixel and server-side in
 *    _tiktokHelper.ts), which improves match quality and stitches a user's
 *    browser + server events together;
 *  - call `ttq.identify()` so browser-side events also carry hashed identity.
 *
 * All functions are safe no-ops on the server and when the pixel is absent.
 */

const EXTERNAL_ID_COOKIE = 'tt_external_id';
const TTCLID_COOKIE = 'ttclid';
const TTCLID_TTL_DAYS = 30; // TikTok click id is valid ~30 days
const EXTERNAL_ID_TTL_DAYS = 365;

/** Best-effort E.164 for an AU store — mirrors the server normalization. */
function normalizePhoneE164(phone?: string | null, defaultCountry = '61'): string | undefined {
  if (!phone) return undefined;
  let p = phone.replace(/[^\d+]/g, '');
  if (!p) return undefined;
  if (p.startsWith('+')) {
    // already international
  } else if (p.startsWith('00')) {
    p = `+${p.slice(2)}`;
  } else if (p.startsWith('0')) {
    p = `+${defaultCountry}${p.slice(1)}`;
  } else if (p.startsWith(defaultCountry)) {
    p = `+${p}`;
  } else {
    p = `+${p}`;
  }
  return p;
}

/** Returns the stable first-party external id, creating it if missing. */
export function getOrCreateTikTokExternalId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  let id = Cookies.get(EXTERNAL_ID_COOKIE);
  if (!id) {
    id = uuidv4();
    Cookies.set(EXTERNAL_ID_COOKIE, id, { expires: EXTERNAL_ID_TTL_DAYS, sameSite: 'Lax' });
  }
  return id;
}

/** Reads `?ttclid=` from the current URL and persists it to a cookie. */
export function captureTtclidFromUrl(): void {
  if (typeof window === 'undefined') return;
  const ttclid = new URLSearchParams(window.location.search).get('ttclid');
  if (ttclid) {
    Cookies.set(TTCLID_COOKIE, ttclid, { expires: TTCLID_TTL_DAYS, sameSite: 'Lax' });
  }
}

/**
 * Calls ttq.identify with whatever identity we have. TikTok accepts plaintext
 * email (it detects "@") and E.164 phone and hashes them before they reach
 * TikTok; external_id is hashed too. Always attaches the stable external_id.
 */
export function identifyTikTok({ email, phone }: { email?: string | null; phone?: string | null } = {}): void {
  if (typeof window === 'undefined') return;
  const ttq = (window as any).ttq;
  if (!ttq || typeof ttq.identify !== 'function') return;

  const payload: Record<string, string> = {};
  const e = email?.trim().toLowerCase();
  if (e) payload.email = e;
  const p = normalizePhoneE164(phone);
  if (p) payload.phone_number = p;
  const ext = getOrCreateTikTokExternalId();
  if (ext) payload.external_id = ext;

  try {
    ttq.identify(payload);
  } catch {
    // never let analytics throw into the calling flow
  }
}

/**
 * Run once per page load (and route change): capture the click id, ensure the
 * external id exists, and attach the anonymous external_id to browser events.
 */
export function initTikTokIdentity(): void {
  if (typeof window === 'undefined') return;
  captureTtclidFromUrl();
  identifyTikTok();
}
